import { FormInputProps, FileType } from "../../models/form.model";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { getS3Filename, upload } from "../../lib/s3upload";
import CircularProgressWithLabel from "../CircularProgressWithLabel";
import {
  FormHelperText,
  FormLabel,
  Typography,
  Stack,
  useTheme,
  Paper,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import {
  UploadFile as UploadFileIcon,
  PreviewOutlined as PreviewOutlinedIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  PictureAsPdf as PictureAsPdfIcon,
  InsertDriveFile as InsertDriveFileIcon,
  PhotoCamera,
} from "@mui/icons-material";
import "../../styles/upload.image.css";
import { ImageSize, parseImageUrl } from "../../lib/helper";

interface UploadedFileObject {
  url: string;
  width?: number | string;
  height?: number | string;
  file?: File;
  acl?: "public-read";
  filename?: string;
}

function FileUploadProgress(
  props: UploadedFileObject & {
    elevation?: number;
    prefix: string;
    onDelete: () => void;
    onFinish: (url: string) => void;
    imageSize?: ImageSize;
    type: FileType;
  },
) {
  const {
    url,
    file,
    prefix,
    onFinish,
    onDelete,
    width,
    height,
    elevation,
    imageSize,
  } = props;
  const [progress, setProgress] = useState<number>();
  const [error, setError] = useState<string>();
  const [filename, setFilename] = useState<string | undefined>(
    file?.name || props.filename,
  );
  useEffect(() => {
    async function up() {
      if (file) {
        setProgress(1);
        try {
          const res = await upload({
            file,
            prefix,
            filename: props.filename,
            onProgress: (p) => {
              const v = ((p.loaded || 0) / (p.total || 1)) * 100;
              setProgress(v);
            },
          });
          setTimeout(() => {
            setProgress(undefined);
            onFinish(res);
          }, 100);
        } catch (error) {
          setError(`Upload ${props.type} failed, please try again`);
        }
      }
    }
    async function getFilename() {
      if (
        (props.type === FileType.pdf || props.type === FileType.file) &&
        props.url &&
        !props.file &&
        !props.filename
      ) {
        setFilename(await getS3Filename(props.url));
      }
    }
    up();
    getFilename();
  }, []);

  return (
    <div>
      <Tooltip title={filename} placement="top">
        <Paper
          elevation={elevation}
          style={{
            width: width,
            height: height,
            border: `1px solid ${error ? "red" : "#ececec"}`,
            borderRadius: 5,
          }}
          className="all-center-wrapper relative image-con"
        >
          {[FileType.image, FileType.avatar].indexOf(props.type) >= 0 && (
            <img
              style={{ maxWidth: "98%", maxHeight: "98%" }}
              alt={url}
              src={parseImageUrl(url, imageSize)}
            />
          )}
          {[FileType.pdf, FileType.file].indexOf(props.type) >= 0 && (
            <div className="all-center-wrapper column-wrapper">
              {props.type === FileType.pdf ? (
                <PictureAsPdfIcon />
              ) : (
                <InsertDriveFileIcon />
              )}
              <Typography
                variant="caption"
                sx={{ px: 1 }}
                className="ellipsis-text"
              >
                {filename}
              </Typography>
            </div>
          )}
          <div className="all-center-wrapper image-action">
            <IconButton
              style={{ color: "white" }}
              onClick={() => {
                if (props.type == FileType.file) {
                  window.open(parseImageUrl(url, ImageSize.none), "_blank");
                  return;
                }
                window.webApp.previewDialog({
                  src:
                    props.type === FileType.pdf
                      ? url
                      : parseImageUrl(url, ImageSize.none),
                  type: props.type,
                  open: true,
                });
              }}
              className="margin-r-normal"
            >
              <PreviewOutlinedIcon />
            </IconButton>
            <IconButton onClick={onDelete} style={{ color: "white" }}>
              <DeleteOutlinedIcon />
            </IconButton>
          </div>
          {progress && <CircularProgressWithLabel value={progress} size={48} data-testid={'fileUploadProgress'} />}
        </Paper>
      </Tooltip>
      {error && (
        <Tooltip title={error} placement="bottom">
          <div
            className="padding-t-normal ellipsis-text"
            style={{ color: "red", maxWidth: width }}
          >
            {error}
          </div>
        </Tooltip>
      )}
    </div>
  );
}

const typeAcceptMap: { [key in FileType]: string } = {
  image: "images/*",
  avatar: "images/*",
  video: "video/*",
  mp3: "*",
  file: "*",
  pdf: "application/pdf",
  qrcode: "*",
  text: "*",
};

export default function FormUploadFile(
  props: FormInputProps & {
    prefix: string;
    multiple?: boolean;
    max?: number;
    filename?: string;
    width?: number | string;
    height?: number | string;
    helperTextClassName?: string;
    type: FileType;
    imageSize?: ImageSize;
    acl?: "public-read";
    dataTestId?: string;
  }
) {
  const {
    name,
    control,
    label,
    rules,
    prefix,
    required,
    multiple,
    setError,
    helperText,
    helperTextClassName,
    filename,
    width = 120,
    height = 120,
    imageSize,
    dataTestId,
  } = props;
  const setErrorMsg = (message: string) => {
    setError(name, { type: "manual", message });
  };
  let max = props.max || 9;
  if (!multiple) {
    max = 1;
  }
  const theme = useTheme();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, value },
        fieldState: { error },
        formState,
      }) => {
        if (!Array.isArray(value)) {
          value = value ? [value] : [];
        }
        value = value.map((v: any) => {
          if (v.url) {
            return v;
          } else {
            return { url: v };
          }
        });
        return (
          <div className="padding-t-normal padding-b-normal">
            {label && (
              <FormLabel
                className="margin-b-normal"
                required={required}
                error={!!error}
              >
                {label}
              </FormLabel>
            )}
            <Stack direction="row" flexWrap={"wrap"}>
              {(value as UploadedFileObject[]).map((v, i) => (
                <Box marginLeft={1} marginTop={1} key={v.url}>
                  <FileUploadProgress
                    {...v}
                    prefix={prefix}
                    acl={props.acl}
                    type={props.type}
                    width={width}
                    height={height}
                    imageSize={imageSize}
                    onDelete={() => {
                      value.splice(i, 1);
                      onChange(multiple ? [...value] : null);
                    }}
                    onFinish={(url) => {
                      value[i] = { url };
                      onChange(multiple ? [...value] : value[0]);
                    }}
                  />
                </Box>
              ))}
              {value.length < max && (
                <Box
                  marginLeft={1}
                  marginTop={1}
                  className="relative all-center-wrapper column-wrapper"
                  style={{
                    height: height,
                    width: width,
                    border: "1px dashed",
                    borderRadius: props.type === FileType.avatar ? 120 : 5,
                    borderColor: theme.palette.primary.main,
                  }}
                  data-testid={dataTestId || name}
                >
                  {props.type === FileType.avatar ? (
                    <PhotoCamera color="primary" />
                  ) : (
                    <UploadFileIcon color="primary" />
                  )}
                  <Typography
                    color="primary"
                    variant="caption"
                    className="margin-t-normal"
                  >
                    {props.type === FileType.avatar
                      ? "Upload photo"
                      : "Click to select file"}
                  </Typography>
                  <input
                    className="width-100p height-100p pointer"
                    style={{
                      position: "absolute",
                      opacity: 0,
                      zIndex: 9,
                    }}
                    accept={typeAcceptMap[props.type]}
                    multiple={multiple}
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files) {
                        return;
                      }
                      try {
                        for (const f of Array.from(files)) {
                          value.push({
                            url: window.URL.createObjectURL(f),
                            file: f,
                            filename,
                          });
                        }
                        onChange(multiple ? [...value] : value[0]);
                      } catch (e: any) {
                        setErrorMsg(e.message);
                      }
                      e.target.value = "";
                    }}
                    type="file"
                    data-testid={`${dataTestId || name}-input`}
                  />
                </Box>
              )}
            </Stack>
            <FormHelperText
              className={`${helperTextClassName || ""} margin-t-normal`}
              error={!!error}
            >
              {error ? error.message : helperText || null}
            </FormHelperText>
          </div>
        );
      }}
    />
  );
}
